export type LayerId = 'structure' | 'thermal' | 'occupancy' | 'services'

export interface LayerSpec {
    id: LayerId
    label: string
    note: string
}

export const LAYERS: Array<LayerSpec> = [
    { id: 'structure', label: 'Structure', note: 'Frame, slabs and roof in bright cream' },
    { id: 'thermal', label: 'Thermal', note: 'Heat loss from cool grey to blue' },
    { id: 'occupancy', label: 'Occupancy', note: 'People and zones in blue' },
    { id: 'services', label: 'Services', note: 'Risers and ducts in blue' }
]

export interface LiveFlag {
    id: string
    text: string
    status: string
    anchor: 'flag1' | 'flag2' | 'flag3'
    note: string
}

export interface ScanEpoch {
    label: string
    date: string
    note: string
}

export interface LiveBuilding {
    slug: string
    name: string
    city: string
    shape: 'library' | 'housing' | 'hall'
    label: string
    flags: Array<LiveFlag>
    epochs: [ScanEpoch, ScanEpoch, ScanEpoch, ScanEpoch]
}

export const SCAN_DATES = ['March 2023', 'September 2023', 'March 2024', 'March 2025']

export const LIVE_BUILDINGS: Array<LiveBuilding> = [
    {
        slug: 'hallmark-library',
        name: 'Hallmark Library',
        city: 'Umeå',
        shape: 'library',
        label: 'Interactive point cloud of Hallmark Library, a three-storey timber library with a stepped roof and a mezzanine reading room.',
        flags: [
            { id: 'FLAG-01', text: 'Slab deflection / Δ 11 mm', status: 'Corrected', anchor: 'flag1', note: 'Mezzanine bay C3 sagged 11 mm under the first book load. Propped and re-levelled before opening.' },
            { id: 'FLAG-02', text: 'Facade moisture', status: 'Monitoring', anchor: 'flag2', note: 'Outer boards on the north-west corner above 20% for six weeks. A flashing was refitted 4 mm higher.' },
            { id: 'FLAG-03', text: 'Beam check / Δ 4 mm', status: 'Within limits', anchor: 'flag3', note: 'Glulam roof beam over the atrium read 4 mm against an allowance of 12 mm.' }
        ],
        epochs: [
            { label: 'Frame', date: SCAN_DATES[0], note: 'CLT frame and slabs up, no fit-out yet.' },
            { label: 'Settlement', date: SCAN_DATES[1], note: 'Mezzanine bay C3 reads 11 mm low under the first loads.' },
            { label: 'Moisture', date: SCAN_DATES[2], note: 'Winter readings show wet boards on the north-west corner.' },
            { label: 'As used', date: SCAN_DATES[3], note: 'Bay corrected, flashing refitted, fit-out complete.' }
        ]
    },
    {
        slug: 'casa-mares',
        name: 'Casa Mares',
        city: 'Lisbon',
        shape: 'housing',
        label: 'Interactive point cloud of Casa Mares, 64 homes in stacked slabs with vertical facade fins.',
        flags: [
            { id: 'FL-01', text: 'Thermal bridge / 1.9 W/m²K', status: 'Repaired', anchor: 'flag1', note: 'A balcony bracket on level 4 carried heat out of the west facade. Insulated in the first summer.' },
            { id: 'FL-02', text: 'Solar gain / 38°', status: 'Designed out', anchor: 'flag2', note: 'Afternoon sun at 38 degrees drove the west rooms over 28 °C. The fins were deepened to 900 mm.' },
            { id: 'FL-03', text: 'Seal failure', status: 'Replaced', anchor: 'flag3', note: 'One window seal on level 6 failed in its first winter. Found by the thermal scan, replaced under warranty.' }
        ],
        epochs: [
            { label: 'Frame', date: SCAN_DATES[0], note: 'Masonry and timber slabs complete, fins not yet fitted.' },
            { label: 'Settlement', date: SCAN_DATES[1], note: 'Ground floor slab settles 5 mm at the west corner.' },
            { label: 'Moisture', date: SCAN_DATES[2], note: 'Condensation behind the failed seal on level 6.' },
            { label: 'As used', date: SCAN_DATES[3], note: 'Seal replaced, bracket insulated, all 64 homes occupied.' }
        ]
    },
    {
        slug: 'ostra-cultural-hall',
        name: 'Ostra Cultural Hall',
        city: 'Gdańsk',
        shape: 'hall',
        label: 'Interactive point cloud of Ostra Cultural Hall, a tiered auditorium under a steel truss, with people shown as points.',
        flags: [
            { id: 'ZN-02', text: 'Queue forming', status: 'Live', anchor: 'flag1', note: 'People counters show the interval crowd stalling at one door. The door was widened and the queue fell by 64%.' },
            { id: 'ZN-04', text: 'CO2 940 ppm', status: 'Live', anchor: 'flag2', note: 'Carbon dioxide above the balcony rises past 900 ppm in full houses. Ventilation now starts 20 minutes earlier.' },
            { id: 'ZN-01', text: 'Door 3 blocked', status: 'Cleared', anchor: 'flag3', note: 'Stage scenery stored against an exit door. Flagged within 90 seconds and cleared by the duty manager.' }
        ],
        epochs: [
            { label: 'Frame', date: SCAN_DATES[0], note: 'Steel truss lifted, raked seating not yet cast.' },
            { label: 'Settlement', date: SCAN_DATES[1], note: 'Truss node T7 reads 8 mm low after the roof deck.' },
            { label: 'Moisture', date: SCAN_DATES[2], note: 'Damp in the lobby slab before the floor finish.' },
            { label: 'As used', date: SCAN_DATES[3], note: 'Open to the public, sensors live in every zone.' }
        ]
    }
]
